// The transport controls (P2.7) — FR-02's play/pause and cue, FR-09's readouts.
//
// **Every button reports a gesture; none of them decides anything.** The state machine lives on the
// audio thread (Architecture §7, `transport.ts`), so `Play` does not know whether it is about to
// start or stop — it sends `play-pressed` and renders whatever phase comes back. That is what makes
// `previewing` renderable at all: it ends by itself when a finger lifts, and a button labelled from
// local state would show "Pause" over a stopped deck.
//
// The readouts are written by hand from a rAF loop, never through React. UX §7 puts waveforms and
// meters "outside the animation system entirely (they're data, not motion design)", and an elapsed
// time is the same kind of thing: sixty reconciles a second to change two digits.

import { useEffect, useRef, type JSX } from 'react';

import type { TrackLoaderApi } from './useTrackLoader';

/**
 * How long before the end FR-09's warning appears.
 *
 * Thirty seconds is the convention across DJ hardware and software, and the reason is the job it
 * does: it is about one phrase plus the time to react. Shorter and it arrives after you needed it;
 * much longer and it is lit for so much of every track that it stops meaning anything.
 */
export const TRACK_END_WARNING_SECONDS = 30;

/**
 * Seconds as `m:ss`, **floored**.
 *
 * Floor here and `round` in `formatDuration` — deliberately opposite, and P2.5 paid for the
 * distinction. A *total* of 9.999977 s is ten seconds to any human, so it rounds. An *elapsed* time
 * of 9.9 s has not reached 0:10 yet, so it floors; rounding it up would make the clock reach 0:10
 * before the audio did, which is exactly wrong for a number a DJ is counting against.
 */
export function formatElapsed(seconds: number): string {
  const whole = Math.max(0, Math.floor(seconds));
  return `${String(Math.floor(whole / 60))}:${String(whole % 60).padStart(2, '0')}`;
}

/**
 * Amplitude → a 0..1 bar fraction, over a −60 dB floor.
 *
 * Decibels rather than linear because hearing is logarithmic: on a linear bar the entire useful
 * range of a quiet passage lives in the first few pixels.
 *
 * This was duplicated from App.tsx's master meter at P2.9, deliberately, because that one was due
 * to retire with the M0 demo — and it did, at P2.12. Chasing a shared helper between a live
 * control and a dying one would have left the wrong copy behind; the surviving copy is this one.
 */
function meterFraction(rms: number): number {
  if (rms <= 0) {
    return 0;
  }
  const db = 20 * Math.log10(rms);
  return Math.min(1, Math.max(0, (db + 60) / 60));
}

export interface TransportProps {
  readonly loader: TrackLoaderApi;
}

export function Transport({ loader }: TransportProps): JSX.Element {
  const elapsedRef = useRef<HTMLSpanElement | null>(null);
  const remainingRef = useRef<HTMLSpanElement | null>(null);
  const warningRef = useRef<HTMLParagraphElement | null>(null);
  const cueMarkerRef = useRef<HTMLSpanElement | null>(null);
  const meterRef = useRef<HTMLDivElement | null>(null);

  // FR-09's readouts and warning, at display rate, straight to the DOM.
  useEffect(() => {
    let frame = 0;

    const tick = (): void => {
      frame = requestAnimationFrame(tick);

      const duration = loader.durationSeconds;
      if (duration === null) {
        return;
      }

      const elapsed = loader.readPositionSeconds();
      const remaining = Math.max(0, duration - elapsed);

      if (elapsedRef.current !== null) {
        elapsedRef.current.textContent = formatElapsed(elapsed);
      }
      if (remainingRef.current !== null) {
        // Negative, because a DJ reads it as "time left", and the minus sign is how every piece of
        // hardware distinguishes it from elapsed at a glance.
        remainingRef.current.textContent = `-${formatElapsed(remaining)}`;
      }
      if (cueMarkerRef.current !== null) {
        cueMarkerRef.current.textContent = formatElapsed(loader.readCueSeconds());
      }

      // This deck's own level (P2.9) — read from its own signal source, not from the master. The
      // difference is the whole point of the prompt: the master meter shows whatever the output bus
      // is doing, and a DJ needs to know what *this deck* is sending into it before the crossfader.
      // Since P3.7 the deck picks the source block from its own id, so this line is what makes two
      // meters move independently rather than in unison.
      const meter = meterRef.current;
      if (meter !== null) {
        const peak = loader.readDeckPeak();
        // Null means nothing was published since the last frame — the deck is not running. Leave the
        // bar where it is rather than dropping it to zero and flickering (P0.7's reasoning).
        if (peak !== null) {
          meter.style.width = `${(meterFraction(peak) * 100).toFixed(1)}%`;
        }
      }

      const warning = warningRef.current;
      if (warning !== null) {
        // `hidden` rather than unmounting: the element is in the DOM either way, so appearing costs
        // no layout and cannot reflow the controls under it mid-mix.
        warning.hidden = remaining > TRACK_END_WARNING_SECONDS || remaining <= 0;
      }
    };

    frame = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(frame);
    };
  }, [loader]);

  const phase = loader.transportPhase;
  const deck = loader.deckId.toUpperCase();

  return (
    <div className="stack">
      <div className="row transport">
        {/* CUE is `pointerdown`/`pointerup`, not `click`, because hold-to-preview is the behaviour.
            A click handler fires on release and could only ever implement a tap. `pointer*` rather
            than `mouse*` so a touchscreen works, and `onLostPointerCapture` covers the finger
            sliding off the button — without it a drag away leaves the deck previewing forever. */}
        <button
          type="button"
          className="transport__cue"
          aria-pressed={phase === 'previewing'}
          onPointerDown={(event) => {
            event.currentTarget.setPointerCapture(event.pointerId);
            loader.pressCue();
          }}
          onPointerUp={() => {
            loader.releaseCue();
          }}
          onLostPointerCapture={() => {
            loader.releaseCue();
          }}
        >
          CUE
        </button>

        <button
          type="button"
          className="transport__play"
          aria-pressed={phase === 'playing'}
          onClick={() => {
            loader.pressPlay();
          }}
        >
          {phase === 'playing' ? 'Pause' : 'Play'}
        </button>

        <span className="micro transport__phase">{phase}</span>
      </div>

      {/* This deck's level. Driven by the rAF loop above via style.width — never by React
          (Stack §2). The tint is chosen by which deck this is, never by the control (UX §5.1). */}
      <div className="stack stack--tight">
        <span className="micro">Deck level</span>
        <span className="bar">
          <div
            ref={meterRef}
            className={`bar__fill bar__fill--deck-${loader.deckId}`}
            role="meter"
            aria-label={`Deck ${deck} level`}
          />
        </span>
      </div>

      <div className="row transport__times">
        <span ref={elapsedRef} className="mono transport__elapsed">
          0:00
        </span>
        <span ref={remainingRef} className="mono transport__remaining">
          -0:00
        </span>
        <span className="micro">
          cue{' '}
          <span ref={cueMarkerRef} className="mono">
            0:00
          </span>
        </span>
      </div>

      {/* The bound keys, read from the source's own table (P2.8) rather than restated here — so a
          remapped key (FR-39) cannot disagree with the hint printed beside it. A placeholder for
          FR-40's proper hold-to-view cheat sheet, which reads the same data. */}
      <ul className="key-hints">
        {loader.keyBindings.map((binding) => (
          <li key={binding.code}>
            <kbd>
              {binding.code === 'Space' ? 'Space' : binding.code.replace('Key', '')}
            </kbd>{' '}
            <span className="micro">{binding.label}</span>
          </li>
        ))}
      </ul>

      {/* FR-09's track-end warning. `--accent-warm` is UX §5.1's reserved semantic for exactly
          this: "record, warnings — never used decoratively". */}
      <p ref={warningRef} className="notice notice--warning" role="status" hidden>
        Track ending — under {TRACK_END_WARNING_SECONDS}s left.
      </p>
    </div>
  );
}
